import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowDown } from 'lucide-react';

/**
 * Cinematic full-bleed hero:
 *   • Clinic photograph as the full-screen backdrop
 *   • Copy overlaid dead-center, floating above the scene
 *   • Brand-tinted veils keep the text legible over the image
 *   • Restrained scroll parallax (backdrop drifts, copy fades)
 */
export default function Hero() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 90]);
  const bgScale = useTransform(scrollY, [0, 700], [1.06, 1.16]);
  const textY = useTransform(scrollY, [0, 700], [0, -70]);
  const fade = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden text-white"
      style={{ background: '#0B0620' }}
    >
      {/* Full-bleed clinic photograph with slow parallax drift */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" aria-hidden="true">
        <motion.img
          src="/hero-clinic.webp"
          alt="Dowakum aesthetic clinic treatment"
          data-testid="hero-image"
          className="w-full h-full object-cover object-[50%_42%]"
          initial={{ opacity: 0, scale: 1.14, filter: 'blur(22px)' }}
          animate={{ opacity: 1, scale: 1.06, filter: 'blur(5px)' }}
          transition={{ delay: 0.25, duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
          loading="eager"
        />
      </motion.div>

      {/* Readability veils — brand-tinted, stronger at the edges */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(11,6,32,0.85) 0%, rgba(16,9,46,0.42) 32%, rgba(11,6,32,0.18) 52%, rgba(11,6,32,0.72) 100%)',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(62% 52% at 50% 40%, rgba(137,72,195,0.30) 0%, transparent 70%)',
          }}
        />
        <div className="absolute inset-0 dwk-noise opacity-[0.25]" />
      </div>

      {/* Centered overlay copy */}
      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-6 md:px-10"
      >
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 1.0, ease: [0.65, 0, 0.35, 1] }}
          className={`${
            isAr
              ? 'font-ar text-[44px] md:text-[64px] lg:text-[80px] leading-[1.12]'
              : 'font-en text-[44px] md:text-[64px] lg:text-[80px] leading-[1.02]'
          } font-bold tracking-tight max-w-5xl`}
          style={{ paddingBottom: '0.18em' }}
          data-testid="hero-headline"
        >
          <span className="block text-white">{t('hero.headline_1')}</span>
          <span className="block dwk-gradient-text-shine" style={{ display: 'block' }}>
            {t('hero.headline_2')}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9 }}
          className={`text-white/85 text-[15px] md:text-[17px] lg:text-[19px] leading-[1.85] max-w-[560px] md:max-w-[640px] ${
            isAr ? 'font-ar' : 'font-en'
          }`}
          data-testid="hero-description"
        >
          {t('hero.description')}
        </motion.p>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1 text-white/50 text-[9px] tracking-[0.45em] uppercase font-en"
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <span>{t('hero.scroll')}</span>
        <motion.span
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowDown size={12} />
        </motion.span>
      </motion.div>
    </section>
  );
}
